import React, { useMemo } from 'react';
import { useI18n } from '@whizz/react-i18n';
import type TopicStudentsEntry from '@whizz/api-client/dist/@types/model/tx3/TopicStudentsEntry';
import type { TopicFocusEntryInterface } from '@whizz/api-model';
import type { OptionTypeBase } from 'react-select';
import type { Cell } from 'react-table';
import type { CustomColumnProps, CustomRowProps } from '../../../../components/table/types';
import type { SetTopicAccessors, StudentCheckboxProps } from './types';
import { ChoiceGroup, Table } from '../../../../components';
import SelectStudentsTableLayout from './SelectStudentsTableLayout';
import { EMPTY_VALUE } from '../../../../util/vars';

type Props = {
  topic: OptionTypeBase | null;
  topicFocusEntries: TopicFocusEntryInterface[];
  selectedTopicStudents: TopicStudentsEntry | null;
  selectedStudentsIds: string[];
  onSelect(student: string): void;
  onSelectAll(enabledStudentsIds: string[]): void;
};

type TopicStatusType = {
  i18nKey: string,
  sortIndex: number,
};

const SortIndex: Record<string, TopicStatusType> = {
  IN_TOPIC_FOCUS: {
    i18nKey: 'pages.TOPIC.SET.MODAL.already_in',
    sortIndex: -1,
  },
  IN_ASSESSMENT: {
    i18nKey: 'pages.TOPIC.SET.MODAL.assessment',
    sortIndex: -2,
  },
  NOT_COVERING: {
    i18nKey: 'pages.TOPIC.SET.MODAL.not_ready',
    sortIndex: -3,
  },
  TOPIC_COMPLETED: {
    i18nKey: 'pages.TOPIC.SET.MODAL.topic_completed',
    sortIndex: -4,
  },
  CURRICULUM_COMPLETED: {
    i18nKey: 'pages.TOPIC.SET.MODAL.curriculum_completed',
    sortIndex: -5,
  },
  HOME: {
    i18nKey: 'pages.TOPIC.SET.MODAL.home_subscription',
    sortIndex: -6,
  },
};

const SetTopicTable = ({ topic, topicFocusEntries, selectedTopicStudents, selectedStudentsIds, onSelect, onSelectAll }: Props): JSX.Element => {
  const { translate } = useI18n();
  const columns = [
    {
      accessor: 'select',
      disableSortBy: true,
      Header: () => null,
      Cell: (cell: Cell) => {
        const values: StudentCheckboxProps = cell.value;
        return (
          <ChoiceGroup
            id={`set_${values.id}`}
            name={'set_students'}
            label={values.label}
            hideLabel
            disabled={values.disabled ?? false}
            checked={values.disabled ? false : selectedStudentsIds.includes(values.id)}
            onChange={() => onSelect(values.id)}
          />
        );
      },
    },
    {
      accessor: 'name',
      Header: translate('components.TABLES.STUDENTS.NAME'),
    },
    {
      accessor: 'topic_age',
      Header: translate('components.TABLES.TOPIC_FOCUS.TOPIC_AGE'),
      Cell: (cell: Cell) => {
        if (cell.value === null) {
          return EMPTY_VALUE;
        } else {
          return cell.value;
        }
      },
    },
  ] as CustomColumnProps<SetTopicAccessors>[];

  const getStudentTopicAgeOrState = (isStudentInSelectedTopicFocus: boolean, topicFocusEntry: TopicFocusEntryInterface) => {
    if (isStudentInSelectedTopicFocus) {
      return SortIndex.IN_TOPIC_FOCUS;
    }

    const studentCovering = selectedTopicStudents?.getCovering().find((studentCovering) => studentCovering.childId === topicFocusEntry.studentId);
    const studentCompleted = selectedTopicStudents?.getCompleted().find((studentCovering) => studentCovering.childId === topicFocusEntry.studentId);

    if (studentCompleted) {
      return SortIndex.TOPIC_COMPLETED;
    }

    if (topicFocusEntry.inAssessment) {
      return SortIndex.IN_ASSESSMENT;
    }

    if (topicFocusEntry.hasHomeSub) {
      return SortIndex.HOME;
    }

    if (null == studentCovering) {
      return SortIndex.NOT_COVERING;
    }

    // Todo: properly determine if student has completed the curriculum
    if (0 === topicFocusEntry.mathsAge) {
      return SortIndex.CURRICULUM_COMPLETED;
    }

    return studentCovering.topicAge;
  };

  const getFormattedData = (): CustomRowProps<SetTopicAccessors>[] => {
    return topicFocusEntries
      .map((topicFocusEntry) => {
        const studentCovering = selectedTopicStudents?.getCovering().find((studentCovering) => studentCovering.childId === topicFocusEntry.studentId);

        const disabled = null == studentCovering
          || null != topicFocusEntry.topicFocus.current
          || topicFocusEntry.inAssessment
          || topicFocusEntry.hasHomeSub
          || 0 === topicFocusEntry.mathsAge
        ;

        const fullName = `${topicFocusEntry.firstname} ${topicFocusEntry.lastname}`;

        const isStudentInSelectedTopicFocus = topicFocusEntry.topicFocus.current != null &&
          selectedTopicStudents?.getTopicName() === topicFocusEntry.topicFocus.current.topic;

        const topicData = getStudentTopicAgeOrState(isStudentInSelectedTopicFocus, topicFocusEntry);

        return {
          select: {
            id: topicFocusEntry.studentId.toString(),
            label: fullName,
            disabled: topic == null || disabled,
          } as StudentCheckboxProps,
          name: fullName,
          topic_age: topic != null ? typeof topicData === 'number' ? topicData : translate(topicData.i18nKey) : null,
          topic_age_index: typeof topicData === 'number' ? topicData : topicData.sortIndex,
        };
      })
      .sort((a, b) => {
        if (a.topic_age_index < 0 || b.topic_age_index < 0) {
          return b.topic_age_index - a.topic_age_index;
        }
        return a.topic_age_index - b.topic_age_index;
      });
  };

  const rows = useMemo(() => getFormattedData(), [ topic ]);

  return (
    <SelectStudentsTableLayout
      id={'set'}
      selectAllProps={{
        indeterminate: selectedStudentsIds.length > 0 && selectedStudentsIds.length !== topicFocusEntries.length,
        checked: selectedStudentsIds.length > 0,
        onChange: () => onSelectAll(rows.filter((row) => !(row.select as StudentCheckboxProps).disabled).map((row) => (row.select as StudentCheckboxProps).id)),
      }}
    >
      <div className={'overflow-auto'} style={{ maxHeight: 300 }}>
        <Table
          id={'set_topic_table'}
          color={'white'}
          cols={columns}
          content={rows}
          initialState={{
            sortBy: [
            ],
          }}
        />
      </div>
    </SelectStudentsTableLayout>
  );
};

SetTopicTable.displayName = 'SetTopicTable';

export default SetTopicTable;
